export interface TrainerChecklistItem {
  id: string
  label: string
  maxScore: number
}

export interface TrainerChecklistSection {
  id: string
  title: string
  items: TrainerChecklistItem[]
}

export const TRAINER_CHECKLIST_SECTIONS: TrainerChecklistSection[] = [
  {
    id: 'before_training',
    title: 'Перед тренировкой / начало тренировки',
    items: [
      {
        id: 'coach_appearance',
        label: 'Внешний вид тренера опрятный, волосы убраны, форма соответствует стандартам',
        maxScore: 2,
      },
      {
        id: 'coach_greeting',
        label: 'Тренер представился / поприветствовал доброжелательно',
        maxScore: 1,
      },
      {
        id: 'contraindications',
        label: 'Тренер спросил о противопоказаниях',
        maxScore: 1,
      },
      {
        id: 'new_clients',
        label: 'Выявил новичков: кто первый раз в студии / на данном занятии, узнал имена',
        maxScore: 1,
      },
      {
        id: 'tactile_correction_warning',
        label: 'Тренер предупредил про применение тактильной коррекции',
        maxScore: 1,
      },
      {
        id: 'client_sensations',
        label:
          'Проговорил, что клиенты должны следить за ощущениями, не допускать боли и сообщать о дискомфорте, чтобы тренер подобрал альтернативные упражнения',
        maxScore: 1,
      },
      {
        id: 'group_ready',
        label: 'Уточнил у группы, готовы ли начать',
        maxScore: 1,
      },
      {
        id: 'training_on_time',
        label: 'Тренировка началась / закончилась вовремя',
        maxScore: 1,
      },
    ],
  },
  {
    id: 'during_training',
    title: 'В течение всего занятия тренер',
    items: [
      {
        id: 'technical_demo',
        label: 'Технический показ упражнений',
        maxScore: 1,
      },
      {
        id: 'exercise_purpose',
        label: 'Проговаривает, для чего выполняются упражнения и что клиент должен чувствовать',
        maxScore: 1,
      },
      {
        id: 'attention_to_clients',
        label: 'Старается уделить внимание всем участникам тренировки: подходит, поправляет, не игнорирует ошибки',
        maxScore: 1,
      },
      {
        id: 'safe_correction',
        label: 'Тянет и поправляет клиенток корректно и безопасно: за твердые части тела',
        maxScore: 1,
      },
      {
        id: 'injury_prevention',
        label: 'Предотвращает случаи травматизма; если таких моментов не было, ставим максимальный балл',
        maxScore: 1,
      },
    ],
  },
  {
    id: 'service',
    title: 'Сохраняет сервис',
    items: [
      {
        id: 'communication_ethics',
        label:
          'Этика общения с клиентами: старается не поворачиваться спиной без необходимости, не отвлекается в телефон, тренировка не проводится в тишине и т.д.',
        maxScore: 3,
      },
      {
        id: 'praises_clients',
        label: 'В течение занятия педагог хвалит клиентов как группу, так и индивидуально',
        maxScore: 2,
      },
      {
        id: 'uses_client_names',
        label: 'Педагог называет клиентов по именам',
        maxScore: 1,
      },
    ],
  },
  {
    id: 'engagement',
    title: 'Харизма и вовлечение клиентов',
    items: [
      {
        id: 'speech_and_communication',
        label:
          'Речь и коммуникация тренера: четкая и уверенная речь, отсутствие слов-паразитов, грубых слов, уменьшительно-ласкательных форм и неуверенных формулировок, понятная подача информации',
        maxScore: 3,
      },
      {
        id: 'atmosphere',
        label:
          'Атмосфера и эмоциональное ведение тренировки: соответствие подачи формату занятия, уверенность тренера, вовлечение клиентов, уместный юмор и комфортная атмосфера',
        maxScore: 3,
      },
    ],
  },
  {
    id: 'training_structure',
    title: 'Структура тренировки',
    items: [
      {
        id: 'warmup_complete',
        label: 'Разминка проведена полностью',
        maxScore: 1,
      },
      {
        id: 'training_blocks',
        label: 'Блоки тренировки соблюдены',
        maxScore: 3,
      },
      {
        id: 'direction_match',
        label: 'Упражнения соответствуют направлению',
        maxScore: 2,
      },
      {
        id: 'equipment_match',
        label: 'Инвентарь соответствует направлению',
        maxScore: 1,
      },
      {
        id: 'no_forbidden_exercises',
        label: 'Нет запрещенных упражнений',
        maxScore: 5,
      },
    ],
  },
  {
    id: 'training_finish',
    title: 'В конце тренировки',
    items: [
      {
        id: 'closing_ritual',
        label:
          'Завершая тренировку, аплодируют друг другу; тренер благодарит группу, желает хорошего дня / вечера, просит аккуратно сложить инвентарь и уточняет, остались ли вопросы',
        maxScore: 2,
      },
      {
        id: 'admin_desk',
        label: 'Сопровождает к стойке администратора для приобретения абонемента',
        maxScore: 2,
      },
    ],
  },
]

export const TRAINER_CHECKLIST_ITEMS = TRAINER_CHECKLIST_SECTIONS.flatMap(section => section.items)
export const TRAINER_CHECKLIST_OPTIONAL_CRITERION_IDS = ['admin_desk'] as const

export const TRAINER_CHECKLIST_MAX_SCORE = TRAINER_CHECKLIST_ITEMS.reduce(
  (sum, item) => sum + item.maxScore,
  0
)

export type TrainerChecklistScores = Record<string, number | null>
export type TrainerChecklistComments = Record<string, string>

export function createEmptyTrainerChecklistScores(): TrainerChecklistScores {
  return Object.fromEntries(TRAINER_CHECKLIST_ITEMS.map(item => [item.id, null]))
}

export function createMaxTrainerChecklistScores(): TrainerChecklistScores {
  return Object.fromEntries(TRAINER_CHECKLIST_ITEMS.map(item => [item.id, item.maxScore]))
}

export function clampTrainerChecklistScore(itemId: string, value: unknown): number {
  const item = TRAINER_CHECKLIST_ITEMS.find(entry => entry.id === itemId)
  const numericValue = Number(value)

  if (!item || !Number.isFinite(numericValue)) return 0

  const normalizedValue = item.maxScore === 1
    ? Math.round(numericValue * 2) / 2
    : Math.round(numericValue)

  return Math.min(item.maxScore, Math.max(0, normalizedValue))
}

export function calculateTrainerChecklistScore(scores: TrainerChecklistScores): number {
  return TRAINER_CHECKLIST_ITEMS.reduce(
    (sum, item) => sum + clampTrainerChecklistScore(item.id, scores[item.id] ?? 0),
    0
  )
}

export function calculateTrainerChecklistMaxScore(scores: TrainerChecklistScores): number {
  return TRAINER_CHECKLIST_ITEMS.reduce((sum, item) => {
    if (isOptionalTrainerChecklistCriterion(item.id) && scores[item.id] == null) {
      return sum
    }

    return sum + item.maxScore
  }, 0)
}

export function countUnevaluatedTrainerChecklistItems(scores: TrainerChecklistScores): number {
  return TRAINER_CHECKLIST_ITEMS.filter(item => scores[item.id] == null).length
}

export function isOptionalTrainerChecklistCriterion(itemId: string): boolean {
  return TRAINER_CHECKLIST_OPTIONAL_CRITERION_IDS.includes(itemId as typeof TRAINER_CHECKLIST_OPTIONAL_CRITERION_IDS[number])
}

export function calculateTrainerChecklistPercent(score: number, maxScore: number = TRAINER_CHECKLIST_MAX_SCORE): number {
  if (maxScore <= 0) return 0
  return score / maxScore * 100
}
